import { ExtendAccessErrorCode } from "@fitness-app/data-models/entities/ExtendAccess";

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Stripe statuses that have a label under `common:subscriptionStatus`. */
const LABELLED_STATUSES = new Set([
  "active",
  "past_due",
  "incomplete_expired",
  "trialing",
  "incomplete",
  "canceled",
  "unpaid",
  "paused",
]);

const OVERDUE_STATUSES = new Set(["past_due", "unpaid"]);

const toShape = (error: unknown) => {
  const shape = (error ?? {}) as { code?: unknown; subscriptionStatus?: unknown; message?: unknown };

  return {
    code: typeof shape.code === "string" ? shape.code : null,
    subscriptionStatus: typeof shape.subscriptionStatus === "string" ? shape.subscriptionStatus : null,
    message: typeof shape.message === "string" && shape.message ? shape.message : null,
  };
};

/**
 * The toast for a refused "change access period" — what `setExtendAccessPeriod`
 * rejected with. A blocked subscription names its status (and, for an overdue
 * one, what unblocks it); any other backend reason is quoted; only a dead
 * connection falls back to "contact support" (TRE-2048).
 */
export const describeExtendAccessPeriodError = (error: unknown, t: Translate): string => {
  const { code, subscriptionStatus, message } = toShape(error);

  if (code === ExtendAccessErrorCode.SubscriptionNotActive && subscriptionStatus) {
    const status = LABELLED_STATUSES.has(subscriptionStatus)
      ? t(`common:subscriptionStatus.${subscriptionStatus}`)
      : subscriptionStatus;
    const reason = t("client:pauseCollection.subscriptionNotActive", { status });

    return OVERDUE_STATUSES.has(subscriptionStatus)
      ? `${reason} ${t("client:pauseCollection.subscriptionPastDueHint")}`
      : reason;
  }

  if (message) {
    return t("client:pauseCollection.cannotChangePeriodWithReason", { reason: message });
  }

  return t("client:pauseCollection.cannotChangePeriod");
};
